import { useCallback, useEffect } from "react"
import { useQuery } from "urql"

import type { GoodsReceipt, GoodsReceiptGetSuccess } from "@/graphql/codegen/graphql"
import { GetGoodsReceiptDocument } from "@/graphql/codegen/graphql"

interface UseGetGoodsReceiptReturn {
  goodsReceipt: GoodsReceipt | null
  isFetching: boolean
  error: Error | undefined
  refetch: () => void
}

interface UseGetGoodsReceiptOptions {
  isPollingEnabled?: boolean
  pollInterval?: number
}

export const useGetGoodsReceipt = (
  goodsReceiptId: string,
  options: UseGetGoodsReceiptOptions = {}
): UseGetGoodsReceiptReturn => {
  const { isPollingEnabled = false, pollInterval = 30000 } = options

  const [{ data, fetching, error }, reexecuteQuery] = useQuery({
    query: GetGoodsReceiptDocument,
    variables: {
      input: { goodsReceiptId },
    },
    requestPolicy: isPollingEnabled ? "network-only" : "cache-first",
  })

  const goodsReceipt =
    data?.goodsReceipt.get.__typename === "GoodsReceiptGetSuccess"
      ? (data.goodsReceipt.get as GoodsReceiptGetSuccess).goodsReceipt
      : null

  const refetch = useCallback(() => reexecuteQuery({ requestPolicy: "network-only" }), [reexecuteQuery])

  useEffect(() => {
    if (!isPollingEnabled) return

    const intervalId = setInterval(() => {
      reexecuteQuery({ requestPolicy: "network-only" })
    }, pollInterval)

    return () => clearInterval(intervalId)
  }, [isPollingEnabled, pollInterval, reexecuteQuery])

  return {
    goodsReceipt,
    isFetching: fetching,
    error,
    refetch,
  }
}
